/**
 * Every tunable gameplay value (SPEC §24). Units: metres, seconds, m/s, percent.
 * Client and server both read this module, so a change here changes both sides.
 */

const DEG = Math.PI / 180;

export const PLAYER = {
  walkSpeed: 5,
  carrySpeed: 4,
  swimSpeed: 2.5,
  sprintSpeed: 7.5,
  crouchSpeed: 2.5,
  crouchHeight: 1.15,
  crouchEyeHeight: 1.0,
  slideEyeHeight: 0.85,
  slideDuration: 0.70,
  slideSprintRequired: 0.30,
  slideCooldown: 0.60,
  diveSpeed: 2,
  /** Passive rise toward the surface when neither dive nor ascend is held. */
  floatUpSpeed: 1,
  maxDiveDepth: 8,
  jumpHeight: 1.1,
  gravity: 20,
  radius: 0.35,
  height: 1.8,
  eyeHeight: 1.62,
  /** Height a walking capsule may step up without jumping. */
  stepHeight: 0.45,
  /** Steepest walkable terrain rise per horizontal metre. */
  maxSlope: 1.25,
  /** Feet height while floating at the surface (head stays above water). */
  surfaceFeetY: -1.35,
  /** Ground deeper than this below sea level means the player swims. */
  swimDepth: -1.2,
  maxHealth: 100,
  safeFallHeight: 6,
  fallDamagePerMetre: 10,
  regenPerSecond: 5,
  regenDelay: 8,
  breathSeconds: 20,
  breathRefillSeconds: 3,
  drowningDps: 15,
  respawnDelay: 4,
  recoverSeconds: 5,
  /** Recover cancels when the player moves further than this from where it started. */
  recoverCancelDistance: 0.25,
  maxPitch: 85 * DEG,
} as const;

export const SHIP = {
  length: 18,
  beam: 6,
  /** Main (waist) deck top; the pier, gangway and boarding all meet this level. */
  deckHeight: 2.5,
  /** Raised aft quarterdeck and forward forecastle tops above the waist deck. */
  quarterdeckRise: 1.2,
  forecastleRise: 0.8,
  /** Hull bottom below the waterline; used for shallow-water collision. */
  draft: 1.9,
  sailSpeeds: [0, 4, 7, 10] as readonly number[],
  acceleration: 1.2,
  drag: 0.6,
  maxTurnRate: 14 * DEG,
  minSteerSpeed: 1,
  /** Speed at which the rudder reaches its full turn rate. */
  fullSteerSpeed: 4,
  anchorStopTime: 3,
  /** Seconds one crewmate pushing the capstan needs to raise the anchor from the bottom. */
  anchorRaiseSeconds: 6,
  /** Each extra crewmate on a capstan bar adds this fraction of one pusher's speed. */
  capstanHelperBoost: 0.6,
  /** Free-running fall from catted to fully paid out after the pawl is kicked off. */
  anchorDropSeconds: 1.6,
  /** Capstan head revolutions for a full raise (and back the other way on a drop). */
  capstanTurns: 1,
  /** Ratchet teeth per revolution; the pawl clicks once per tooth while raising. */
  capstanTeeth: 12,
  /** Length of the dropping sailor's pawl kick (presentation only; movement stays free). */
  anchorKickSeconds: 0.55,
  emptyStopTime: 5,
  unstickDelay: 3,
  unstickMaxSpeed: 2,
  damageImpactSpeed: 3,
  /** Speed the hull is pushed back with after a collision. */
  bounceSpeed: 1.5,
  /** Deceleration (m/s²) of the sideways shove a rammed hull takes through the water. */
  shoveDrag: 1.2,
  /** Speed (m/s) a hull drifts away from whatever it touched, so it never pins itself against a pier or rock. */
  fendOffSpeed: 0.8,
  leakSlots: 4,
  floodPerLeak: 1.5,
  /** Bucket bailing: hold E in flood water to fill, then throw it over the rail. */
  bucketFillSeconds: 1.2,
  /** Whole throw swing; the water leaves the bucket `bucketThrowRelease` seconds in. */
  bucketThrowSeconds: 0.75,
  bucketThrowRelease: 0.4,
  /** Flooding (percent) one thrown bucket removes. */
  bucketVolume: 10,
  /** Flood water is visible (and can be scooped) above this level. */
  bucketMinFlood: 1,
  /** Farthest the thrower's centre may stand from the outboard deck edge. */
  bucketRailReach: 0.9,
  /** Minimum cosine between the thrower's facing and the edge's outward normal (about 70°). */
  bucketFacingCos: 0.35,
  repairSeconds: 2.5,
  repairCancelDistance: 1,
  sinkSeconds: 5,
  /** Collision circles along the keel (ship-local z) with their radius. */
  hullCircles: [
    { z: 7.0, r: 2.2 },
    { z: 2.4, r: 3.0 },
    { z: -2.4, r: 3.1 },
    { z: -7.0, r: 2.9 },
  ] as readonly { z: number; r: number }[],
  /** Rudder response rate toward the held A/D input, per second. */
  rudderRate: 3,
} as const;

export const WORLD = {
  size: 6000,
  half: 3000,
  boundaryWarning: 200,
  seaFloor: -22,
  digSpotsPerIsland: 3,
} as const;

export const INTERACT = {
  promptRange: 2.5,
  serverRange: 3.25,
  /** Reach of a ladder's foot (its head: half) within which walking into it mounts it. */
  ladderRange: 1.2,
  /** Reach of ladder ends on a deck (mast foot, hull-ladder head): tight, so crossing the deck past one never mounts it. */
  deckLadderReach: 0.6,
  /** Horizontal push into (or, at the head, out of) the rungs that mounts a ladder, 0 … 1. */
  ladderMountPush: 0.3,
  /** Seconds for a full ladder at full input; the climber sets pace and direction. */
  ladderSeconds: 2.2,
  /** Metres a jump off the rungs hops out from the ladder face. */
  ladderJumpOff: 0.35,
  coneHalfAngle: 30 * DEG,
  digRadius: 3,
  digHits: 5,
  digCooldown: 0.7,
  /** Merchant accepts a chest when the carrier is this close. */
  sellRange: 3.25,
} as const;

/** Authoritative cutlass duel timing and geometry. */
export const MELEE = {
  range: 2.4,
  halfAngle: Math.PI / 3,
  blockHalfAngle: Math.PI / 3,
  combo: [
    { windup: 0.20, active: 0.10, recovery: 0.30, damage: 25 },
    { windup: 0.18, active: 0.10, recovery: 0.32, damage: 25 },
    { windup: 0.28, active: 0.12, recovery: 0.46, damage: 30 },
  ],
  queueWindow: 0.20,
  missRecovery: 0.18,
  blockedRecovery: 0.12,
  parryWindow: 0.16,
  parryCooldown: 0.50,
  parryStagger: 0.45,
} as const;

export const PISTOL = {
  damage: 55, range: 60, capacity: 6, maxAmmo: 24, reloadSeconds: 3, recoverySeconds: 0.35, recoilSeconds: 0.35,
  aimMagnification: 1.5, aimTransitionSeconds: 0.24, reducedAimTransitionSeconds: 0.08,
} as const;

export const SPYGLASS = { magnification: 4, transitionSeconds: 0.25, reducedTransitionSeconds: 0.08, avatarRange: 1000 } as const;

/** Island fixtures: mounted telescopes, gate levers and plunder crates. */
export const FIXTURES = {
  telescopeMagnification: 8,
  leverCooldown: 1.2,
  crateHits: 2,
  crateRespawn: 240,
  crateGold: [15, 45],
  cratePlanks: 2,
  crateBalls: 3,
  crateRounds: 3,
} as const;

export const CREW_TITLES = [
  { gold: 0, title: "Driftwood Deckhands" },
  { gold: 1000, title: "Salt-Stained Crew" },
  { gold: 5000, title: "Lantern-Lit Raiders" },
  { gold: 15000, title: "Legends of the Tiny Seas" },
] as const;

export const CREW = {
  maxCrews: 25,
  maxSize: 4,
  names: ["Red Gulls", "Blue Marlins", "Green Turtles", "Gold Crabs", "Black Eels", "Silver Sharks", "Coral Squids", "Iron Barnacles", "Storm Petrels", "Amber Rays", "Jade Dolphins", "Crimson Krakens", "Ivory Pelicans", "Violet Urchins", "Copper Seals", "Teal Herons", "Ember Pufferfish", "Frost Narwhals", "Sable Ravens", "Rose Starfish", "Moss Tortoises", "Indigo Mantas", "Saffron Gannets", "Slate Walruses", "Pearl Oysters"],
  colors: ["#c8443a", "#2f6fa3", "#4f8a3c", "#f2c14e", "#222831", "#9aa5b1", "#ff7f6b", "#5b5f66", "#4a6fa5", "#e0a040", "#2e9e8f", "#a4161a", "#efe6d2", "#7b4fa3", "#b87333", "#3a9db8", "#ff5f1f", "#bfe3f2", "#3d3b4f", "#e59ab4", "#6b8f3a", "#3b3f9e", "#f4c430", "#5c6b73", "#f8f4e8"],
} as const;

export const CHEST_TIERS = [
  { name: "Castaway's Chest", value: 500 },
  { name: "Seafarer's Chest", value: 1200 },
  { name: "Captain's Chest", value: 3000 },
] as const;
export const QUEST = { huntLegs: 3, huntTier: 2, mapTierOdds: [.75, .22, .03], boardIslands: 8, huntNextIslands: 6 } as const;
export const NPC = {
  ships: 3,
  names: ["Bilge Rats", "Black Gulls", "Drowned Dogs"],
  colors: ["#3a3a3a", "#5b3f72", "#7a3b2e"],
  sailorNames: ["Grim Gus", "Scurvy Sid", "Rotten Ruth", "Bones Bart", "Dead-Eye Dot", "Gravel Greg", "Mouldy Meg", "Black Bess", "Rusty Rick"],
  firstSpawnDelay: 45, respawnDelay: 90, minSpawnDistance: 600, aggroRange: 400, leashRange: 650,
  safeRadius: 350, broadsideRange: 160, fireRange: 180, waypointReach: 60,
  aimErrorYaw: .03, aimErrorElevation: .012, fireDelay: 1, decisionInterval: .5, bailRate: 3.5,
  /** Hull shots aim this high above the waterline; a `rigShotChance` share go high at `rigAimHeight` to cripple sails and rigging. */
  hullAimHeight: 1, rigShotChance: 0.3, rigAimHeight: 8.5,
} as const;
export const BOTS = {
  crewNames: ["Barnacle Bob", "Salty Sue", "Peg-Leg Pete", "Cutlass Kate", "One-Eye Ollie", "Rum-Barrel Ron"],
  offShipRecoverDelay: 3, taskTimeout: 8, taskCooldown: 5, bailThreshold: 5,
} as const;
export const SHIP_SLOTS = CREW.maxCrews + NPC.ships;

export const CANNON = {
  ballSpeed: 55,
  gravity: 12,
  reloadSeconds: 3,
  /** Max yaw away from straight out of the side. */
  yawLimit: 0.9,
  minElevation: -0.15,
  maxElevation: 0.45,
  /** Barrel pivot height above the deck. */
  pivotHeight: 1.0,
  barrelLength: 1.2,
  ballRadius: 0.2,
  playerDamage: 60,
  /** Flooding added per severity level of a blow (shot or ram) landing on a hole already stove in. */
  fullHullFlood: 10,
  lifetime: 8,
  /** Collision samples per tick along the trajectory. */
  substeps: 4,
} as const;

export const SELF_CANNON = {
  speed: CANNON.ballSpeed,
  gravity: CANNON.gravity,
  muzzleClearance: PLAYER.radius * 2 + 0.05,
  maxStepDistance: PLAYER.radius / 2,
} as const;

export const SUPPLY = {
  startBalls: 20,
  startPlanks: 10,
  barrelBalls: 10,
  barrelPlanks: 5,
  barrelCooldown: 120,
  /** The crew ship must be anchored this close to an island barrel. */
  barrelShipRange: 60,
} as const;

/** Per-crew ship cosmetics; ships replicate indices into these lists. */
export const LIVERY = {
  figureheads: ["lion", "mermaid", "kraken", "eagle"],
  emblems: ["skull", "anchor", "compass", "kraken"],
  flags: ["skull", "swords", "anchor", "star"],
} as const;

/** Light ship upgrade tiers 0..maxTier; each tier also shows on the model. */
export const UPGRADES = {
  maxTier: 2,
  hull: { floodScale: [1, 0.85, 0.7] },
  guns: { reloadScale: [1, 0.9, 0.8] },
  hold: { maxBalls: [40, 50, 60], maxPlanks: [20, 25, 30] },
} as const;

export const VOTE = {
  duration: 20,
  cooldown: 10,
} as const;

/** Room weather (`weatherAt`): visual only, derived from the room's seed and server time. */
export const WEATHER = {
  /** Seconds between weather keyframes; the wind eases from one to the next over the whole span. */
  period: 300,
  /** Wind speed range, m/s; low winds are likelier than gales. */
  windMin: 6,
  windMax: 17,
  /** The wind veers up to this far either side of the room's prevailing heading, degrees. */
  headingRange: 30,
  /** Storminess rises from 0 to 1 across this wind speed band, m/s. */
  stormStart: 12.5,
  stormFull: 16,
} as const;

export const NET = {
  tickRate: 30,
  patchRateHz: 20,
  inputRate: 30,
  inputTimeout: 0.25,
  interpolationDelay: 0.1,
  maxMessagesPerSecond: 60,
  maxInteractionsPerSecond: 10,
  /** Hard cap enforced by Colyseus itself (disconnects). */
  hardMessageCap: 120,
  /** Violations within the window before a client is kicked. */
  maxViolations: 50,
  violationWindow: 10,
  /** Inputs that may be simulated per server tick when catching up. */
  maxInputsPerTick: 3,
  inputBuffer: 8,
  maxPlayers: 100,
  reconnectWindow: 30,
} as const;

export const ROOM_NAME = "tiny_seas";
export const PUBLIC_ROOM_ID = "sea";
export type GameMode = "sea" | "royale";
export const ROYALE_ROOM_NAME = "tiny_seas_royale";
export const ROYALE_ROOM_ID = "royale";
export const BATTLE_ROYALE = {
  /** Open sea ≥1000 m from every island and collider (guarded by packages/shared/test/royale.test.ts). */
  center: { x: 900, z: 1300 },
  spawnRadius: 450,
  ringStart: 600,
  ringEnd: 80,
  shrinkDelay: 60,
  shrinkDuration: 420,
  /** Ships beyond this sink at once; hidden island terrain begins ~1020 m out. */
  hardRadius: 900,
  ringFloodPerSecond: 4,
  countdown: 20,
  endedSeconds: 15,
  treasureDelay: 45,
  treasureHold: 300,
  treasureSpread: 0.5,
  lootStart: 4,
  lootInterval: 15,
  lootMax: 8,
  lootSpread: 0.85,
  lootPickupRadius: 9,
  lootBalls: 10,
  lootPlanks: 5,
  minShips: 4,
  /** NPC gunnery in the arena: wider per-shot aim error and a longer pause between shots than open-sea NPCs. */
  npcAimErrorYaw: 0.1,
  npcAimErrorElevation: 0.04,
  npcFireDelay: 3,
} as const;
export const NAME_MAX = 16;

export const DT = 1 / NET.tickRate;
/** Launch speed that reaches `jumpHeight` under the fixed-step (semi-implicit Euler) integrator. */
export const JUMP_VELOCITY = (PLAYER.gravity * DT) / 2 + Math.sqrt(((PLAYER.gravity * DT) / 2) ** 2 + 2 * PLAYER.gravity * PLAYER.jumpHeight);
